import { type JSX, useCallback, useEffect, useState } from "react";

import { type InvocationResult, invokeOperation } from "../api/catalog";
import { type UsageBar, UsageChart } from "./UsageChart";

/** The operation this panel replaces the generic form for. */
export const USAGE_OPERATION = "get_llm_usage_v1_usage_llm_get";

/** What AMS will group by. Repeat the parameter for several, but a chart wants one axis. */
const DIMENSIONS = [
  { key: "model", label: "Model" },
  { key: "operation", label: "Operation" },
  { key: "agent", label: "Agent" },
  { key: "day", label: "Day" },
  { key: "job", label: "Job" },
  { key: "request", label: "Request" },
] as const;

type Dimension = (typeof DIMENSIONS)[number]["key"];

/** Which response field carries the category for each grouping. */
const CATEGORY_FIELD: Record<Dimension, string> = {
  model: "model",
  operation: "operation",
  agent: "agent_id",
  day: "day",
  job: "job_id",
  request: "request_id",
};

/** Which filter parameter narrows to one value of that dimension. */
const FILTER_PARAM: Record<Dimension, string> = {
  model: "model",
  operation: "operation",
  agent: "agent_id",
  day: "from",
  job: "job_id",
  request: "request_id",
};

const RANGES = [
  { key: "all", label: "All time", days: null },
  { key: "1", label: "Today", days: 1 },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
] as const;

interface UsageRow {
  agent_id: string | null;
  job_id: string | null;
  request_id: string | null;
  model: string | null;
  operation: string | null;
  day: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  call_count: number;
}

interface Filters {
  agent_id?: string;
  model?: string;
  operation?: string;
  job_id?: string;
  request_id?: string;
  from?: string;
}

/**
 * Token spend as a chart rather than a table of numbers.
 *
 * This route is an aggregation — it takes a required `group_by` and a set of narrowing filters —
 * which is a graph waiting to happen, and reading it as raw JSON wastes that. So the panel drives
 * the same endpoint the generic form would, through the same `/api/invoke` (so identity, scope and
 * envelope handling are unchanged), and draws the answer.
 *
 * Filters sit in one row above the content and scope everything below them, so the chart, the
 * headline numbers and the table always agree.
 */
export function UsagePanel(): JSX.Element {
  const [dimension, setDimension] = useState<Dimension>("operation");
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("all");
  const [filters, setFilters] = useState<Filters>({});
  const [rows, setRows] = useState<UsageRow[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setProblem(null);
    const since = RANGES.find((entry) => entry.key === range)?.days ?? null;
    const query: Record<string, unknown> = { group_by: [dimension], limit: 200, ...filters };
    if (since !== null) {
      const from = new Date(Date.now() - (since - 1) * 86_400_000);
      from.setHours(0, 0, 0, 0);
      query["from"] = from.toISOString();
    }
    try {
      const result: InvocationResult = await invokeOperation({
        operation_id: USAGE_OPERATION,
        query,
      });
      if (!result.ok) {
        setProblem(result.problem?.detail ?? `The service answered HTTP ${result.status}.`);
        setRows([]);
        return;
      }
      const data = (result.data ?? {}) as { rows?: UsageRow[]; truncated?: boolean };
      setRows(data.rows ?? []);
      setTruncated(Boolean(data.truncated));
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dimension, range, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fold rows that display as the same category before drawing them.
  //
  // The service can return several rows that reduce to one label -- grouping by request gives a
  // row per request id, and every call with no request attribution comes back as its own null
  // row. Drawing those as separate identical bars is meaningless, and it produced two bars
  // labelled "unattributed" sitting on top of each other.
  const merged = new Map<string, UsageBar>();
  for (const row of rows ?? []) {
    const label = String(row[CATEGORY_FIELD[dimension] as keyof UsageRow] ?? "unattributed");
    const seen = merged.get(label);
    merged.set(label, {
      label,
      first: (seen?.first ?? 0) + row.prompt_tokens,
      second: (seen?.second ?? 0) + row.completion_tokens,
      total: (seen?.total ?? 0) + row.total_tokens,
      extra: [
        { label: "Calls", value: (seen?.extra?.[0]?.value ?? 0) + row.call_count },
      ],
    });
  }

  const bars: UsageBar[] = [...merged.values()]
    // Time reads chronologically; every other dimension reads largest-first.
    .sort((a, b) => (dimension === "day" ? a.label.localeCompare(b.label) : b.total - a.total));

  const totals = bars.reduce(
    (sum, bar) => ({
      prompt: sum.prompt + bar.first,
      completion: sum.completion + bar.second,
      total: sum.total + bar.total,
      calls: sum.calls + (bar.extra?.[0]?.value ?? 0),
    }),
    { prompt: 0, completion: 0, total: 0, calls: 0 },
  );

  const active = Object.entries(filters).filter(([, value]) => value);

  return (
    <section className="panel">
      <header className="panel__header">
        <div>
          <h2>LLM token usage</h2>
          <p className="muted">
            What the workers spent, grouped and filtered. Prompt and completion are shown apart
            because they are priced apart.
          </p>
        </div>
        <span className="badge-row">
          {truncated && (
            <span className="flag flag--warn" title="Rows were dropped; these are not totals">
              truncated
            </span>
          )}
          <button type="button" onClick={() => void load()} disabled={loading}>
            Refresh
          </button>
        </span>
      </header>

      {/* One row, above everything it scopes. Date range first. */}
      <div className="filters">
        <label>
          <span>Period</span>
          <select value={range} onChange={(event) => setRange(event.target.value as typeof range)}>
            {RANGES.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Group by</span>
          <select
            value={dimension}
            onChange={(event) => setDimension(event.target.value as Dimension)}
          >
            {DIMENSIONS.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Model</span>
          <input
            value={filters.model ?? ""}
            placeholder="any"
            onChange={(event) => setFilters({ ...filters, model: event.target.value })}
          />
        </label>

        <label>
          <span>Operation</span>
          <input
            value={filters.operation ?? ""}
            placeholder="any"
            onChange={(event) => setFilters({ ...filters, operation: event.target.value })}
          />
        </label>

        <label>
          <span>Agent</span>
          <input
            value={filters.agent_id ?? ""}
            placeholder="any"
            onChange={(event) => setFilters({ ...filters, agent_id: event.target.value })}
          />
        </label>

        {active.length > 0 && (
          <button type="button" className="link-button" onClick={() => setFilters({})}>
            Clear {active.length} filter{active.length === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {problem && (
        <div className="notice notice--error">
          <strong>Could not read usage.</strong> {problem}
        </div>
      )}

      {rows !== null && rows.length === 0 && !problem && (
        <div className="notice notice--warning">
          <strong>No usage recorded for this slice.</strong> Either nothing has called the LLM yet,
          or the provider is the stub — the health panel says which. Ask a question and look again.
        </div>
      )}

      {bars.length > 0 && (
        <>
          {/* A handful of headline numbers is a KPI row, not another chart. */}
          <dl className="kpis">
            <div>
              <dt>Total tokens</dt>
              <dd>{totals.total.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Prompt</dt>
              <dd>{totals.prompt.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Completion</dt>
              <dd>{totals.completion.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Calls</dt>
              <dd>{totals.calls.toLocaleString()}</dd>
            </div>
          </dl>

          {/* Refetching holds the previous render rather than flashing a skeleton. */}
          <div className={loading ? "chart-frame chart-frame--stale" : "chart-frame"}>
            <UsageChart
              bars={bars}
              series={{ first: "Prompt tokens", second: "Completion tokens" }}
              orientation={dimension === "day" ? "vertical" : "horizontal"}
              {...(dimension === "day"
                ? {}
                : {
                    // Clicking a mark narrows to it, which is the natural way to drill in.
                    // Days are the one dimension where that would mean a range, not a value.
                    onPick: (label: string) => {
                      if (label === "unattributed") {
                        return;
                      }
                      setFilters({ ...filters, [FILTER_PARAM[dimension]]: label });
                    },
                  })}
            />
          </div>

          <button type="button" className="link-button" onClick={() => setShowTable(!showTable)}>
            {showTable ? "Hide the numbers" : "Show the numbers"}
          </button>

          {/* The table view: every value in the chart is reachable without hovering. */}
          {showTable && (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{DIMENSIONS.find((d) => d.key === dimension)?.label}</th>
                    <th>Prompt</th>
                    <th>Completion</th>
                    <th>Total</th>
                    <th>Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {bars.map((bar) => (
                    <tr key={bar.label}>
                      <td>{bar.label}</td>
                      <td>{bar.first.toLocaleString()}</td>
                      <td>{bar.second.toLocaleString()}</td>
                      <td>{bar.total.toLocaleString()}</td>
                      <td>{(bar.extra?.[0]?.value ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
